// @ts-check

import { historyCompletedStamp } from '../../shared/room-progress.js';

/**
 * Wall-clock milliseconds when a finished room was completed.
 *
 * `rooms.completed_at` is the finish wall-clock, written once. Duration
 * `completed_time` values are not dates. Older rows fall back to a Unix-ms
 * `completed_time`, then `last_activity_at`.
 *
 * @param {{
 *   is_completed?: number|boolean|null,
 *   completed_at?: number|null,
 *   completed_time?: number|null,
 *   last_activity_at?: number|null
 * }|null|undefined} room
 * @returns {number|null}
 */
export function adminRoomCompletedAt(room) {
  if (!room?.is_completed) return null;
  const stamp = historyCompletedStamp(room);
  return stamp > 0 ? stamp : null;
}

/**
 * Operator-facing status for one admin Rooms row.
 *
 * Owner-deleted rooms stay labelled "User Deleted" even when they were still
 * in progress, so they are not mistaken for live puzzles.
 *
 * @param {{
 *   is_completed?: number|boolean|null,
 *   deleted_at?: number|string|null,
 *   deleted_reason?: string|null
 * }} room
 * @returns {{ label: string, className: string }}
 */
export function adminRoomStatus(room) {
  const reason = String(room?.deleted_reason || '');
  if (room?.deleted_at && reason === 'owner') {
    return { label: 'User Deleted', className: 'status-inactive' };
  }
  if (room?.is_completed) {
    return { label: 'Completed', className: 'status-active' };
  }
  return { label: 'In Progress', className: 'status-progress' };
}

/**
 * Combined live-session / mover count, e.g. `1 / 0`.
 *
 * The left number is currently connected players. The right number is unique
 * players who have snapped a piece to another piece or locked one on the board.
 *
 * @param {number} [activeSessions]
 * @param {number} [totalPlayers]
 * @returns {string}
 */
export function formatActiveTotalPlayers(activeSessions, totalPlayers) {
  const active = Number(activeSessions);
  const total = Number(totalPlayers);
  return (Number.isFinite(active) ? active : 0) + ' / ' + (Number.isFinite(total) ? total : 0);
}
